import { z } from 'zod';
export const moneyCentsSchema = z.number().int().nonnegative();
export const productTypeSchema = z.enum([
    'single_class',
    'credit_pack',
    'trial',
]);
export const bookingStatusSchema = z.enum([
    'reserved',
    'awaiting_payment',
    'confirmed',
    'payment_failed',
    'expired',
    'cancelled',
]);
export const paymentStatusSchema = z.enum([
    'created',
    'open',
    'pending',
    'authorized',
    'paid',
    'failed',
    'canceled',
    'expired',
    'partially_refunded',
    'refunded',
    'chargeback',
]);
export const purchaseStatusSchema = z.enum([
    'pending',
    'paid',
    'refunded',
    'partially_refunded',
    'cancelled',
    'free',
]);
export const invoiceStatusSchema = z.enum([
    'not_required',
    'pending',
    'created',
    'failed',
]);
export const archiveStatusSchema = z.enum([
    'pending',
    'generated',
    'failed',
    'not_required',
]);
export const jobTypeSchema = z.enum([
    'mollie-sales-invoice.create',
    'mollie-sales-invoice.sync',
    'invoice-archive.generate',
    'booking-confirmation.send',
    'booking-cancellation.send',
    'verification-code.send',
    'booking-release-expired',
    'payment-reconciliation',
    'refund-reconciliation',
]);
export const jobStatusSchema = z.enum([
    'pending',
    'running',
    'completed',
    'failed',
    'dead',
]);
/** Convert integer CHF cents to Mollie amount string, e.g. 2550 -> "25.50" */
export function formatCentsAsMollieAmount(cents) {
    if (!Number.isInteger(cents) || cents < 0) {
        throw new Error(`Invalid amount in cents: ${cents}`);
    }
    return (cents / 100).toFixed(2);
}
/** Convert Mollie amount string to cents */
export function mollieAmountToCents(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        throw new Error(`Invalid Mollie amount: ${value}`);
    }
    return Math.round(parsed * 100);
}
/** Format vat rate as Mollie expects, e.g. 810 -> "8.10" for 8.1% stored as basis points */
export function formatVatRate(vatRateBps) {
    return (vatRateBps / 100).toFixed(2);
}
export function computeVatFromGrossInclusive(grossCents, vatRateBps) {
    const vatCents = Math.round((grossCents * vatRateBps) / (10000 + vatRateBps));
    return {
        grossCents,
        vatCents,
        netCents: grossCents - vatCents,
    };
}
export const createBookingBodySchema = z.object({
    classSessionId: z.string().uuid(),
    productId: z.string().uuid(),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: z.string().trim().email().max(255),
    phone: z.string().trim().max(40).optional(),
    street: z.string().trim().min(1).max(200),
    streetNumber: z.string().trim().min(1).max(20),
    postalCode: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(100),
    country: z.string().trim().length(2).default('CH'),
    returnOrigin: z.string().url(),
    returnPath: z.string().min(1).max(500),
});
export const requestAccessCodeBodySchema = z.object({
    email: z.string().trim().email().max(255),
});
export const verifyAccessCodeBodySchema = z.object({
    email: z.string().trim().email().max(255),
    code: z.string().trim().regex(/^\d{6}$/),
});
export const redeemCreditBookingBodySchema = z.object({
    classSessionId: z.string().uuid(),
    creditPassId: z.string().uuid(),
    returnOrigin: z.string().url(),
    returnPath: z.string().min(1).max(500),
});
export const creditPassSummarySchema = z.object({
    id: z.string().uuid(),
    productName: z.string(),
    creditsTotal: z.number().int().positive(),
    creditsRemaining: z.number().int().nonnegative(),
    validUntil: z.string().datetime().nullable(),
});
export const bookingStatusResponseSchema = z.object({
    bookingId: z.string().uuid(),
    bookingStatus: bookingStatusSchema,
    paymentStatus: paymentStatusSchema.nullable(),
    invoiceStatus: invoiceStatusSchema,
    invoiceNumber: z.string().nullable(),
    reference: z.string(),
    classTitle: z.string().nullable(),
    startsAt: z.string().datetime().nullable(),
    creditsRemaining: z.number().int().nonnegative().nullable().optional(),
});
//# sourceMappingURL=index.js.map